import { a4PrintConfig } from "./commonConfig";

let printFrame = null;
let stylesReady = false;
let prefetchScheduled = false;

/**
 * Clone live stylesheets into the print iframe.
 * Much faster than serializing every cssRule from styleSheets.
 */
const injectAppStyles = (doc) => {
  if (stylesReady && doc.getElementById("fast-print-app-styles")) return;

  const existing = doc.getElementById("fast-print-app-styles");
  if (existing) existing.remove();

  const frag = doc.createDocumentFragment();
  const marker = doc.createElement("meta");
  marker.id = "fast-print-app-styles";
  frag.appendChild(marker);

  document
    .querySelectorAll('link[rel="stylesheet"], style')
    .forEach((node) => {
      const clone = node.cloneNode(true);
      // Absolutize stylesheet hrefs so they resolve inside the iframe.
      if (node.tagName === "LINK" && node.href) {
        clone.setAttribute("href", node.href);
      }
      frag.appendChild(clone);
    });

  doc.head.appendChild(frag);
  stylesReady = true;
};

const clonePrintNode = (node) => {
  const clone = node.cloneNode(true);
  if (clone instanceof HTMLElement) {
    clone.setAttribute("aria-hidden", "true");
  }

  // Copy resolved image URLs so the clone hits the browser cache immediately.
  const sourceImgs = node.querySelectorAll("img");
  const cloneImgs = clone.querySelectorAll("img");
  const len = Math.min(sourceImgs.length, cloneImgs.length);
  for (let i = 0; i < len; i += 1) {
    const source = sourceImgs[i];
    const img = cloneImgs[i];
    img.decoding = "async";
    img.loading = "eager";
    const src = source.currentSrc || source.src;
    if (src && img.src !== src) img.src = src;
  }
  return clone;
};

const areSourceImagesReady = (node) => {
  const imgs = node.querySelectorAll("img");
  if (!imgs.length) return true;
  for (let i = 0; i < imgs.length; i += 1) {
    const img = imgs[i];
    if (!(img.complete && (img.naturalWidth > 0 || img.naturalHeight > 0))) {
      return false;
    }
  }
  return true;
};

const ensurePrintFrameReady = (doc, pageStyle = a4PrintConfig) => {
  injectAppStyles(doc);

  let styleEl = doc.getElementById("fast-print-page-style");
  if (!styleEl) {
    styleEl = doc.createElement("style");
    styleEl.id = "fast-print-page-style";
    doc.head.appendChild(styleEl);
  }
  if (styleEl.textContent !== pageStyle) {
    styleEl.textContent = pageStyle;
  }
};

const createPrintIframe = () => {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("tabindex", "-1");
  iframe.setAttribute("title", "print-frame");
  Object.assign(iframe.style, {
    position: "fixed",
    top: "0",
    left: "0",
    width: "0",
    height: "0",
    border: "0",
    margin: "0",
    padding: "0",
    opacity: "0",
    visibility: "hidden",
    pointerEvents: "none",
    zIndex: "-1",
    overflow: "hidden",
  });
  document.body.appendChild(iframe);
  return iframe;
};

const getPrintDocument = (pageStyle = a4PrintConfig) => {
  if (printFrame?.contentDocument) {
    return {
      iframe: printFrame,
      doc: printFrame.contentDocument,
    };
  }

  const iframe = createPrintIframe();
  printFrame = iframe;

  const doc = iframe.contentDocument;
  doc.open();
  doc.write(
    '<!DOCTYPE html><html><head><meta charset="utf-8"><title></title></head><body></body></html>',
  );
  doc.close();

  ensurePrintFrameReady(doc, pageStyle);

  return { iframe, doc };
};

const syncPrintContent = (node, doc) => {
  if (!node) return;
  doc.body.replaceChildren(clonePrintNode(node));
};

/** Create the hidden iframe + inject styles early (idle). */
export const prefetchPrintFrame = (pageStyle = a4PrintConfig) => {
  if (typeof window === "undefined") return;
  try {
    const { doc } = getPrintDocument(pageStyle);
    ensurePrintFrameReady(doc, pageStyle);
  } catch {
    // Ignore prefetch errors.
  }
};

const schedulePrefetch = () => {
  if (prefetchScheduled || typeof window === "undefined") return;
  prefetchScheduled = true;
  const run = () => prefetchPrintFrame();
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 2500 });
  } else {
    window.setTimeout(run, 1200);
  }
};

schedulePrefetch();

export const warmPrintFrame = (node, pageStyle = a4PrintConfig) => {
  if (!node || typeof window === "undefined") return;
  const { doc } = getPrintDocument(pageStyle);
  ensurePrintFrameReady(doc, pageStyle);
  syncPrintContent(node, doc);
};

export const printFromNode = (
  node,
  { title = "", pageStyle = a4PrintConfig } = {},
) => {
  if (!node || typeof window === "undefined") return;

  const previousActive = document.activeElement;
  const { iframe, doc } = getPrintDocument(pageStyle);
  doc.title = title || "";
  ensurePrintFrameReady(doc, pageStyle);
  syncPrintContent(node, doc);

  const win = iframe.contentWindow;
  let restored = false;
  const restoreUi = () => {
    if (restored) return;
    restored = true;
    try {
      if (
        previousActive &&
        previousActive !== document.body &&
        typeof previousActive.focus === "function"
      ) {
        previousActive.focus({ preventScroll: true });
      }
    } catch {
      // Ignore focus restore errors.
    }
    try {
      win?.removeEventListener?.("afterprint", restoreUi);
    } catch {
      // Ignore.
    }
  };

  const invokePrint = () => {
    win?.addEventListener?.("afterprint", restoreUi);
    try {
      win?.focus?.();
    } catch {
      // Ignore.
    }
    win?.print?.();
    window.setTimeout(restoreUi, 750);
  };

  // Source images already loaded => browser cache serves the clone instantly.
  if (areSourceImagesReady(node)) {
    invokePrint();
    return;
  }

  const images = Array.from(doc.images ?? []);
  if (!images.length) {
    invokePrint();
    return;
  }

  let printed = false;
  const finish = () => {
    if (printed) return;
    printed = true;
    invokePrint();
  };

  // Short wait only; don't block the print dialog on slow assets.
  const deadline = window.setTimeout(finish, 50);

  let pending = 0;
  images.forEach((img) => {
    if (img.complete) return;
    pending += 1;
    const done = () => {
      pending -= 1;
      if (pending <= 0) {
        window.clearTimeout(deadline);
        finish();
      }
    };
    img.onload = done;
    img.onerror = done;
  });

  if (pending === 0) {
    window.clearTimeout(deadline);
    finish();
  }
};
